import { Flex } from '@radix-ui/themes';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  AnonymousHitterAvatar,
  CommonHitterAvatar,
} from 'components/common/hitters/avatar';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CommonHitterSessionStats } from 'components/common/hitters/session-stats';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { SessionHitsTable } from 'components/sections/analytics/session-hits/table';
import { SessionHitsToolbar } from 'components/sections/analytics/session-hits/toolbar';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  ANONYMOUS_HITTER,
  ANONYMOUS_HITTER_ID,
} from 'lib_ts/enums/hitters.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { HittersService } from 'services/hitters.service';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';

interface IProps {
  backToPitches: () => void;
}

export const PitchListHittingResults = (props: IProps) => {
  const { machine } = useContext(MachineContext);
  const { isMetric } = machine;

  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const hits = useAnalyticsStore((s) => s.hits);

  const sessionStart = useAuthStore((s) => s.sessionStart);

  const hitters = useHittersStore((s) => s.hitters);
  const activeHitter = useHittersStore((s) => s.activeHitter);
  const touched = useHittersStore((s) => s.touched);

  const sessionHitters = useMemo(() => {
    // hitters that have faced a pitch in this session
    const values = hitters.filter((h) => Object.keys(touched).includes(h._id));

    // if the active hitter has not faced a pitch yet - add them to the list
    if (activeHitter && !values.find((h) => h._id === activeHitter?._id)) {
      values.push(activeHitter as IHitter);
    }

    return orderBy(values, ['lastActive'], ['desc']);
  }, [hitters, touched, activeHitter]);

  const hitterOptions = useMemo<IOptionExt[]>(() => {
    const sessionHitterOptions: IOptionExt[] = sessionHitters.map((h) => ({
      label: h.name,
      value: h._id,
      suffix: <HitterSideBadge value={h.side} />,
      prefix: <CommonHitterAvatar size="1" hitter={h} clickDisabled />,
    }));

    return [
      {
        label: t('hitters.anonymous'),
        value: ANONYMOUS_HITTER_ID,
        prefix: <AnonymousHitterAvatar size="1" />,
      },
      ...sessionHitterOptions,
    ];
  }, [sessionHitters]);

  const [selectedHitter, setSelectedHitter] = useState<IHitter | undefined>(
    activeHitter || ANONYMOUS_HITTER
  );

  const hitterID = PitchListHelper.getSafeHitterID(selectedHitter);

  const [sessionID, setSessionID] = useState('');

  useEffect(() => {
    if (!sessionStart) {
      return;
    }

    let cancelled = false;

    HittersService.getInstance()
      .getLatestSessionByHitterID({
        newerThan: sessionStart.toISOString(),
        hitterID: selectedHitter?._id ?? null,
      })
      .then((session) => {
        if (!cancelled) {
          setSessionID(session?._id ?? '');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionStart, selectedHitter?._id]);

  return (
    <ErrorBoundary identifier="PitchListHittingResults">
      <Flex height="100%" direction="column">
        <FlexTableWrapper
          gap="1"
          header={
            <Flex direction="column" gap="3">
              <SessionHitsToolbar
                hitterOptions={hitterOptions}
                selectedHitter={selectedHitter}
                setSelectedHitterId={(value) => {
                  if (value === ANONYMOUS_HITTER_ID) {
                    setSelectedHitter(ANONYMOUS_HITTER);
                    return;
                  }

                  setSelectedHitter(
                    sessionHitters.find((h) => h._id === value) as IHitter
                  );
                }}
              />
              <CommonHitterSessionStats
                loading={loading}
                isMetric={!!isMetric}
                hits={hits}
              />
            </Flex>
          }
          table={
            <SessionHitsTable
              identifier="AnalyticsHitterSessionTable"
              sessionID={sessionID}
              hitterID={hitterID}
              backToPitches={props.backToPitches}
              machineID={machine.machineID}
              skipHeader
            />
          }
        />
      </Flex>
    </ErrorBoundary>
  );
};
